// Вставка файлов из буфера обмена.
//
// Сама ничего не грузит: превращает буфер в File[] и отдаёт в ту же функцию, что
// обслуживает file input и drop, — так наследуются валидация, последовательность
// загрузки (сервер считает position как MAX+1) и тосты.
//
// В отличие от drop, вставка не указывает на элемент: событие приходит туда, где
// стоит курсор ввода. Поэтому зоны регистрируются в общем стеке, а слушатель на
// document один на всё приложение.

import { useEffect, useRef, useState, type RefObject } from "react"
import { clipboardHasFiles, filesFromClipboard, normalizePastedFiles } from "@/lib/clipboard-files"
import { DIALOG_WINDOW_SELECTOR } from "@/lib/dialog-layer"
import { filterReferenceFilesByMode, type ReferenceFileAcceptMode } from "@/lib/reference-file-policy"

export type PasteFilesScope =
  /** Ловит вставку, только когда курсор ввода или мышь внутри зоны. */
  | "container"
  /** Ещё и подхватывает вставку «мимо всех зон» — для главной панели экрана. */
  | "page"

type PasteZoneHandlers = {
  accept: ReferenceFileAcceptMode
  onFiles: (files: File[]) => void | Promise<void>
  onRejected?: () => void
}

type PasteZone = {
  element: HTMLElement
  scope: PasteFilesScope
  handlers: RefObject<PasteZoneHandlers>
  setPasting: (value: boolean) => void
}

const zones: PasteZone[] = []
let pointer: { x: number; y: number } | null = null

function zoneFromNode(node: Node | null): PasteZone | null {
  let element = node instanceof Element ? node : null
  while (element) {
    const found = zones.find((zone) => zone.element === element)
    if (found) return found
    element = element.parentElement
  }
  return null
}

/** Курсор ввода стоит в тексте — значит пользователь печатает, а не целится мышью. */
function typingInText(target: EventTarget | null): boolean {
  const element = target instanceof Element ? target : null
  return element?.closest('input,textarea,[contenteditable="true"]') != null
}

function pickZone(target: EventTarget | null): PasteZone | null {
  const byFocus = zoneFromNode(target instanceof Node ? target : null)
  if (byFocus) return byFocus

  // Наведением целятся только когда не печатают: иначе мышь, случайно замершая над
  // соседней полосой, уводила бы вставку из той, где идёт работа.
  if (pointer && !typingInText(target)) {
    const byPointer = zoneFromNode(document.elementFromPoint(pointer.x, pointer.y))
    if (byPointer) return byPointer
  }

  // Последняя смонтированная выигрывает: панель, открытая поверх, забирает вставку
  // у той, что под ней.
  for (let i = zones.length - 1; i >= 0; i -= 1) {
    const zone = zones[i]
    if (zone.scope === "page") return zone
  }
  return null
}

/** Открытая модалка перекрывает страницу — зона под ней вставку получать не должна. */
function coveredByDialog(zone: PasteZone): boolean {
  return Array.from(document.querySelectorAll(DIALOG_WINDOW_SELECTOR)).some((dialog) => !dialog.contains(zone.element))
}

async function acceptFiles(zone: PasteZone, files: File[]): Promise<void> {
  const { accept, onFiles, onRejected } = zone.handlers.current
  zone.setPasting(true)
  try {
    const batch = filterReferenceFilesByMode(await normalizePastedFiles(files), accept)
    if (batch.length === 0) {
      onRejected?.()
      return
    }
    await onFiles(batch)
  } finally {
    zone.setPasting(false)
  }
}

function handlePaste(event: ClipboardEvent): void {
  // Порядок проверок — жёсткий: пока не убедились, что в буфере файлы, событие не
  // трогаем вовсе. Иначе пострадала бы обычная вставка текста, в том числе
  // TokenPaste в редакторе промпта (он работает по text/plain).
  if (event.defaultPrevented) return
  if (!clipboardHasFiles(event.clipboardData)) return

  const zone = pickZone(event.target)
  if (!zone || coveredByDialog(zone)) return

  const files = filesFromClipboard(event.clipboardData)
  if (files.length === 0) return

  event.preventDefault()
  void acceptFiles(zone, files)
}

function handlePointerMove(event: PointerEvent): void {
  pointer = { x: event.clientX, y: event.clientY }
}

function registerZone(zone: PasteZone): () => void {
  if (zones.length === 0) {
    document.addEventListener("paste", handlePaste)
    document.addEventListener("pointermove", handlePointerMove, { passive: true })
  }
  zones.push(zone)

  return () => {
    const index = zones.indexOf(zone)
    if (index >= 0) zones.splice(index, 1)
    if (zones.length === 0) {
      document.removeEventListener("paste", handlePaste)
      document.removeEventListener("pointermove", handlePointerMove)
      pointer = null
    }
  }
}

export type UsePasteFilesArgs = {
  /** Контейнер зоны: его поддерево задаёт «вставка пришла сюда». */
  targetRef: RefObject<HTMLElement | null>
  onFiles: (files: File[]) => void | Promise<void>
  accept?: ReferenceFileAcceptMode
  scope?: PasteFilesScope
  enabled?: boolean
  /** В буфере были файлы, но ни один не подошёл по типу. */
  onRejected?: () => void
}

export function usePasteFiles({
  targetRef,
  onFiles,
  accept = "image",
  scope = "container",
  enabled = true,
  onRejected,
}: UsePasteFilesArgs): { pasting: boolean } {
  const [pasting, setPasting] = useState(false)
  const handlers = useRef<PasteZoneHandlers>({ accept, onFiles, onRejected })
  handlers.current = { accept, onFiles, onRejected }

  useEffect(() => {
    const element = targetRef.current
    if (!enabled || !element) return

    return registerZone({ element, scope, handlers, setPasting })
    // targetRef — ref, в deps не нужен; зона пересобирается при смене режима.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scope])

  return { pasting }
}
